/**
 * @Author: Fernando Sanchez
 */
// -- React Stuff -- //
import React, { useEffect, useRef } from "react";
// -- Material UI Components & Stuff -- //
import { styled } from "@mui/system";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Link,
    Stack,
    Typography
} from "@mui/material";
// -- Assets
import logo from "../../images/logotipo.svg";
import microsoft from "../../images/microsoft.svg";
import background from "../../assets/images/app_background.webp";
import backgroundMedium from "../../assets/images/app_background-960.webp";
import backgroundJpg from "../../assets/images/app_background.jpg";
// -- Auth
import { useAuth } from "../../contexts/AuthContext";
import {CircleOutlined} from "@mui/icons-material";

function LoginMicrosoft(props) {

    // microsoft
    const { login, isLoading } = useAuth();
    const autoLoginStarted = useRef(false);

    useEffect(() => {
        const media = "(min-width: 900px)";
        const existingPreload = document.querySelector('link[data-login-background-preload="true"]');
        const preload = existingPreload || document.createElement("link");

        preload.rel = "preload";
        preload.as = "image";
        preload.href = background;
        preload.media = media;
        preload.setAttribute("imagesrcset", `${backgroundMedium} 960w, ${background} 1600w`);
        preload.setAttribute("imagesizes", "(min-width: 1200px) 60vw, 50vw");
        preload.setAttribute("data-login-background-preload", "true");

        if (!existingPreload) {
            document.head.appendChild(preload);
        }

        return () => {
            preload.remove();
        };
    }, []);

    useEffect(() => {
        const isLegacyInvoiceOpen = window.location.pathname === '/invoices/legacy-open';

        if (!isLegacyInvoiceOpen || isLoading || autoLoginStarted.current) {
            return;
        }

        autoLoginStarted.current = true;
        login();
    }, [isLoading, login]);

    const MyGrid = styled(Grid)({
        height: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: 0,
    });

    // bacjground color could be random
    const DivLogotypeContainer = styled('div')(({ theme }) => ({
        background: `#707070 url(${backgroundJpg}) center / cover no-repeat`,
        backgroundImage: `image-set(
            url(${backgroundMedium}) 1x,
            url(${background}) 2x
        )`,
        width: "60%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        [theme.breakpoints.down("md")]: {
            width: "50%",
        },
        [theme.breakpoints.down("md")]: {
            display: "none",
        },
    }));

    const FormContainer = styled('div')(({ theme }) => ({
        width: "40%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: '10vh',
        [theme.breakpoints.down("md")]: {
            width: "100%",
            paddingTop: 0
        },
    }));

    const Form = styled('div')(({ theme }) => ({
        width: 320,
    }));

    const Greeting = styled(Typography)(({ theme }) => ({
        fontWeight: 500,
        textAlign: "center",
        marginTop: theme.spacing(4),
    }));

    const MicrosoftButton = styled(Button)(({ theme }) => ({
        marginTop: theme.spacing(6),
        boxShadow: theme.shadows[2],
        backgroundColor: "white",
        width: "100%",
        textTransform: "none",
    }));

    const MicrosoftIcon = styled('img')(({ theme }) => ({
        width: 30,
        marginRight: theme.spacing(2),
    }));

    const FormDividerContainer = styled('div')(({ theme }) => ({
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
        display: "flex",
        alignItems: "center",
    }));

    const FormDivider = styled('div')(({ theme }) => ({
        flexGrow: 1,
        height: 1,
        backgroundColor: theme.palette.text.hint + "40",
    }));

    return (
        <MyGrid container>
            <DivLogotypeContainer />
            <FormContainer>
                <Form sx={{minHeight: `80vh`}} >
                    <React.Fragment>
                        <Stack textAlign={'center'} >
                            <Box
                                sx={{
                                    height: '100px',
                                    alignContent: 'center',
                                    marginTop: '20px'
                                }}
                            >
                                <Link href="https://padilla-bujalil.com.mx" underline="none" target="_blank">
                                    <img
                                        src={logo} alt={'logo'}
                                    />
                                </Link>
                            </Box>
                        </Stack>
                        <Box>
                            <Divider sx={{ color: 'gray' }}></Divider>
                        </Box>
                        <Greeting variant="h1">
                            Bienvenido!
                        </Greeting>
                        <Stack textAlign={'center'}>
                            {
                                isLoading ?
                                    (
                                        <Box sx={{marginTop: '20px'}}>
                                            <CircularProgress size={50} />
                                            <Typography variant={'h6'}>Cargando...</Typography>
                                            <Typography>Por favor sigue las instrucciones de Microsoft</Typography>
                                        </Box>
                                    ):
                                    (
                                        <MicrosoftButton
                                            size="large"
                                            onClick={login}>
                                            <MicrosoftIcon src={microsoft} alt="microsoft logo"></MicrosoftIcon>
                                            &nbsp;Continuar con Microsoft
                                        </MicrosoftButton>
                                    )
                            }
                            {!isLoading && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        maxWidth: 320,
                                        mt: 2,
                                        mx: 'auto',
                                        color: 'text.secondary',
                                        lineHeight: 1.5
                                    }}
                                >
                                    Al continuar usted acepta los{' '}
                                    <Link href="/tyc.html" target="_blank" rel="noopener noreferrer">
                                        términos y condiciones
                                    </Link>
                                    {' '}de uso.
                                </Typography>
                            )}
                        </Stack>
                        <FormDividerContainer>
                            <FormDivider/>
                        </FormDividerContainer>
                    </React.Fragment>
                </Form>
                <Stack textAlign='center' sx={{maxHeight: `20vh`}} alignItems={'center'} alignContent={'center'}>

                    <Link underline='none' href='https://anti-ia.org/' target='_blank'>
                        <CircleOutlined sx={{ color: '#00FF00' }}/>
                    </Link>
                </Stack>
            </FormContainer>
        </MyGrid>
    );
}

export default LoginMicrosoft;
